<template>
  <div class="report-card">
    <div class="pa-3 report-card-grid">
      <div class="d-flex flex-column align-center justify-center">
        <div class="icon-container" :class="{ 'icon-container-error': isFailed, 'icon-container-loader': isEnqueued }">
          <mf-icon fill size="24" :color="isFailed ? 'vermelho-0' : 'cinza-d10'" icon="forward_to_inbox" />
        </div>
        <span class="file-type-text">.{{ report.fileFormat }}</span>
      </div>

      <div class="content-container">
        <div class="d-flex align-center">
          <mf-tooltip top max-width="300px">
            <template v-slot:activator="{ on }">
              <h3 class="mb-0" v-on="on">Relatório | {{ report.title }}</h3>
            </template>
            <span>Relatório | {{ report.title }}</span>
          </mf-tooltip>

          <span class="hour">{{ getTime }}</span>
        </div>

        <div class="d-flex align-center justify-space-between mt-2">
          <div class="job-status-container d-flex align-center">
            <div class="job-status" :style="`background-color: ${jobStatusColor[report.state]};`" />
            <p class="mb-0 mx-1">{{ jobStatus[report.state] }}</p>
          </div>

          <div class="d-flex align-center">
            <sending-list :emails="report.emails" />

            <div v-show="isDownloadable">
              <mf-tooltip top max-width="200px" :disabled="!isExpired">
                <template v-slot:activator="{ on }">
                  <span
                    v-if="isFinished"
                    class="action-btn d-flex align-center ml-3"
                    :class="isExpired ? 'disabled-action-btn' : ''"
                    v-on="on"
                    @click="downloadFiles"
                  >
                    <mf-icon fill :color="isExpired ? 'cinza-l60' : 'marca-0-l40'" size="14" class="mr-1">download</mf-icon>
                    Baixar arquivos
                  </span>
                </template>
                <span>Relatório expirado</span>
              </mf-tooltip>
            </div>

            <div v-show="isDownloadable">
              <mf-tooltip left max-width="250px" :disabled="allowResendReport">
                <template v-slot:activator="{ on }">
                  <span
                    v-if="isFailed"
                    :class="`${allowResendReport ? 'action-btn' : 'disabled-btn'} d-flex align-center ml-3`"
                    @click="allowResendReport && $emit('resend-report', report.id)"
                  >
                    <mf-icon fill :color="`${allowResendReport ? 'marca-0-l40' : 'cinza-0'}`" size="14" class="mr-1">autorenew</mf-icon>
                    Reenviar relatório
                  </span>
                </template>
                <span>Não é possível reenviar este relatório.</span>
              </mf-tooltip>
            </div>
          </div>
        </div>

        <div class="d-flex align-center justify-space-between content-footer mt-2">
          <p class="ma-0">{{ report.user.name }}</p>
          <span v-if="isFinished">Data de expiração: {{ getDate }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import SendingList from './SendingList.vue'
export default {
  name: 'ReportCard',
  components: { SendingList },
  props: {
    report: {
      type: Object,
      default: () => {},
    },
    allowResendReport: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    jobStatus: {
      ENQUEUED: 'Na fila',
      FAILED: 'Erro',
      FINISHED: 'Enviado',
    },
    jobStatusColor: {
      ENQUEUED: '#F3AF00',
      FAILED: '#E03210',
      FINISHED: '#0A8533',
    },
  }),
  computed: {
    isFinished() {
      return this.report.state === 'FINISHED'
    },
    isEnqueued() {
      return this.report.state === 'ENQUEUED'
    },
    isFailed() {
      return this.report.state === 'FAILED'
    },
    getTime() {
      if (!this.report.createdAt) return '-'
      return DateTime.fromISO(this.report.createdAt.toString()).toFormat('HH:mm')
    },
    getDate() {
      if (!this.report.expiresAt) return '-'
      return DateTime.fromISO(this.report.expiresAt.toString()).toFormat('dd/MM/yyyy')
    },
    isExpired() {
      if (!this.report.expiresAt) return true
      const reportDate = DateTime.fromISO(this.report.expiresAt.toString())
      const currentDate = DateTime.local()

      return currentDate > reportDate
    },
    isDownloadable() {
      const { filesUrl, fileUrl, user } = this.report
      const loggedUser = JSON.parse(window.localStorage.getItem('loggedUser'))
      return user.id === loggedUser?.id_user || filesUrl?.length > 0 || fileUrl
    },
  },
  methods: {
    downloadFiles() {
      if (this.isExpired) return

      const { filesUrl, fileUrl, name } = this.report
      if (filesUrl && filesUrl.length > 0) {
        filesUrl.forEach((item) => this.download(item, name))
      } else {
        this.download(fileUrl, name)
      }
    },
    download(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
    },
  },
}
</script>

<style lang="scss" scoped>
.report-card {
  border-bottom: 1px solid $cinza-l70;
  color: $cinza-d10;
  font-size: 14px;

  .report-card-grid {
    display: grid;
    align-items: center;
    grid-template-columns: 44px 1fr;
    gap: 10px;
  }
}

.file-type-text {
  color: $cinza-d10;
  font-size: 12px;
  font-weight: 600;
}

.icon-container {
  width: 44px;
  height: 44px;
  background: $cinza-l80;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.icon-container-error {
  background-color: rgba($vermelho-0, 0.2);
}

.icon-container-loader::before {
  content: url('../../assets/reportExportTrackingLoader.png');
  position: absolute;
  top: 0;
  left: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  animation: spin 1.2s linear infinite reverse;
}

.content-container {
  h3 {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 295px;
    margin-right: 8px;
    color: $cinza-d10;
    font-weight: 700;
    font-size: 14px;
  }

  .hour {
    color: $cinza-l30;
  }

  .job-status-container {
    .job-status {
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }

    p {
      color: $cinza-l10;
    }

    span {
      color: $cinza-d10;
    }
  }

  .action-btn {
    color: $marca-0-l40;
    cursor: pointer;
    user-select: none;
  }
  .disabled-btn {
    color: $cinza-0;
    cursor: not-allowed;
    user-select: none;
  }

  .disabled-action-btn {
    cursor: not-allowed;
    color: $cinza-l60;
  }

  .content-footer {
    p {
      color: $cinza-l10;
    }

    span {
      color: $cinza-l30;
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
